import {
	ActionBar,
	Input,
	SpinnerWithBackground,
	TextArea,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { useLoopStore } from '../store'
import type { LoopGeneralTabInput } from './types'

export const LoopGeneralTab = () => {
	const { organizationId: _organizationId, loopId: _loopId } = useParams<{
		organizationId: string
		loopId: string
	}>()
	const navigate = useNavigate()
	const toast = useToast()
	const { loop, useCreateLoop, useEditLoop } = useLoopStore()
	const { createLoop, isLoading: isCreating } = useCreateLoop()
	const { editLoop, isLoading: isEditing } = useEditLoop(loop?.id)

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty },
		reset,
	} = useForm<LoopGeneralTabInput>({
		defaultValues: {
			name: loop?.name || '',
			description: loop?.description || '',
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	useEffect(() => {
		if (loop) {
			reset({
				name: loop.name || '',
				description: loop.description || '',
			})
		}
	}, [loop, reset])

	const handleSubmitInternal = async (data: LoopGeneralTabInput) => {
		if (!loop) return

		try {
			if (loop.id) {
				await editLoop({
					name: data.name,
					description: data.description || null,
				})
			} else {
				await createLoop({
					name: data.name,
					description: data.description || null,
					textQuestionLabel: null,
					ratingQuestionLabel: null,
					textQuestionEnabled: true,
					ratingQuestionEnabled: true,
					ratingScale: 5,
					scheduleType: 'one-time',
					scheduleFrequency: null,
					scheduleStartDate: null,
					scheduleEndDate: null,
					emailTemplateId: null,
					populationId: null,
				})
			}
			reset(data)
		} catch (err) {
			// Mutation errors are handled by the mutation's onError callback.
			// Surface non-mutation errors (e.g., missing store data) to the user.
			if (err instanceof Error && !(err as any).data?.code) {
				toast.showToast(err.message, 'error')
			}
		}
	}

	const isPending = isCreating || isEditing

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 xl:gap-x-12 gap-6 px-6 items-start'>
				<div>
					<Controller
						name='name'
						control={control}
						rules={{
							required: 'Name is required',
						}}
						render={({ field }) => (
							<Input
								label='Loop Name'
								placeholder='e.g., Weekly Team Check-in'
								variant={errors.name ? 'error' : 'default'}
								hint={errors.name?.message as string}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								required
							/>
						)}
					/>
				</div>
				<div>
					<Controller
						name='description'
						control={control}
						render={({ field }) => (
							<TextArea
								label='Description (Optional)'
								placeholder='Describe the purpose of this loop'
								value={field.value || ''}
								onChange={field.onChange}
								onBlur={field.onBlur}
								rows={4}
							/>
						)}
					/>
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate('/loops'),
					},
					{
						variant: 'primary' as const,
						label: loop?.id ? 'Save' : 'Create Loop',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
